import { SwatchBorderStyle, TableBorderStyle, Pattern } from '@common/api';
import { styled } from 'goober';
import { forwardRef } from 'react';
import { FlexCol, IsolateAbsolute } from '@components/layout/Flex';
import { getLuminance } from '@components/chart';

export const GridScroller = styled(
    'div',
    forwardRef
)(() => ({
    overflow: 'auto',
    width: '100%',
    height: '100%',
}));

export const Grid = styled('div')<{ $background?: string }>(({ $background }) => ({
    display: 'flex',
    flexWrap: 'nowrap',
    cursor: 'default',
    background: $background,
    color: $background && getLuminance($background) < 0.5 ? 'white' : 'inherit',
    width: 'fit-content', // Needed so that position: sticky works scrolled all the way to the right
}));

export const GridColumn = styled('div')(() => ({
    width: 'auto',
    minWidth: 'auto',
    flexBasis: 'fit-content',
}));

export const GridColumnInner = styled(FlexCol)(() => ({
    overflow: 'hidden',
    paddingBottom: '2px',
}));

export const GridItem = styled(
    'div',
    forwardRef
)<{ $depth?: number }>(({ $depth }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1.2,
    paddingLeft: $depth ? `${Math.max(0, $depth - 1) * 10 + 2}px` : undefined,
    paddingRight: '2px',

    '& .visible-on-hover': {
        display: 'none',
    },
    '&:hover .visible-on-hover': {
        display: 'block',
    },
}));

export const GridColumnResizeGuide = styled('div')<{ $align: 'left' | 'center' | 'right' }>(({ theme, $align }) => ({
    position: 'absolute',
    inset: 0,
    top: '4px',
    bottom: '4px',
    right: '-1px',
    pointerEvents: 'none',
    borderLeft: $align !== 'left' ? `1px solid ${theme.colors.neutral.w200}` : undefined,
    borderRight: $align !== 'right' ? `1px solid ${theme.colors.neutral.w200}` : undefined,
}));

export const GridColumnStickyLeft = styled(GridColumn)<{ $background?: string; $zIndex?: number }>(
    ({ $background, $zIndex }) => ({
        position: 'sticky',
        left: 0,
        zIndex: $zIndex,
        display: 'flex',
        flexDirection: 'column',
        background: $background,
    })
);

export const GridItemStickyTop = styled(GridItem)<{ $background?: string; $zIndex?: number }>(
    ({ $background, $zIndex }) => ({
        position: 'sticky',
        top: 0,
        zIndex: $zIndex,
        background: $background,
    })
);

export const GridHeaderEllipsis = styled('div')<{ $lineClamp: number }>(({ $lineClamp }) => ({
    margin: '4px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: $lineClamp,
}));

export const GridRowEllipsis = styled('div')(() => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

export const GridRowIcon = styled('div')<{ $canClick?: boolean; $inline?: boolean; $color?: string }>(
    ({ $canClick, $inline, $color }) => ({
        position: 'relative',
        width: !$inline ? '24px' : undefined,
        top: !$inline ? '-1px' : undefined,
        cursor: $canClick ? 'pointer' : 'default',
        color: $color,
        flexShrink: 0,
    })
);

export const GridRowText = styled(GridRowEllipsis)<{
    $left?: string | 0;
    $align?: 'left' | 'center' | 'right';
    $color?: string;
    $flexGrow?: number;
    $flexShrink?: number;
    $width?: number;
}>(({ $left, $align, $color, $flexGrow, $flexShrink, $width }) => ({
    position: 'relative',
    left: $left,
    flexGrow: $flexGrow,
    flexShrink: $flexShrink,
    textAlign: $align,
    color: $color,
    width: $width ? `${$width}px` : undefined,
    alignSelf: 'center',
}));

export const GridRowCell = styled(
    'div',
    forwardRef
)<{
    $align?: 'left' | 'center' | 'right';
    $verticalAlign?: 'start' | 'center' | 'end';
    $overflow?: string;
    $flexGrow?: number;
    $color?: string;
}>(({ $align, $verticalAlign = 'center', $overflow, $flexGrow, $color }) => ({
    display: 'flex',
    position: 'relative',
    justifyContent: $align ? { left: 'flex-start', center: 'center', right: 'flex-end' }[$align] : undefined,
    textAlign: $align,
    alignItems: $verticalAlign,
    overflow: $overflow,
    height: '100%',
    flexGrow: $flexGrow,
    color: $color,
    gap: '5px',
}));

export const GridItemHeaderBorder = styled('div')<{
    $borderStyle?: TableBorderStyle;
    $background?: string;
    $color?: string;
}>(({ $borderStyle, $background, $color }) => ({
    position: 'absolute',
    width: '100%',
    left: 0,
    top: '100%',
    zIndex: 1,

    ...($borderStyle == TableBorderStyle.Thin
        ? { height: '1px', background: $color }
        : $borderStyle == TableBorderStyle.Thick
        ? { height: '3px', background: $color, marginTop: '-1px' }
        : $borderStyle == TableBorderStyle.Double
        ? {
              height: '3px',
              background: $background,
              marginTop: '-1px',
              borderTop: `1px solid ${$color}`,
              borderBottom: `1px solid ${$color}`,
          }
        : $borderStyle == TableBorderStyle.Hatched
        ? {
              height: '3px',
              marginTop: '-1px',
              background: `repeating-linear-gradient(-45deg, ${$background} 0 3px, ${$color} 3px 6px)`,
          }
        : {}),
}));

export const GridChartOrigin = styled('div')<{
    $left?: string | 0;
    $color?: string;
    $background?: string;
    $borderStyle?: TableBorderStyle;
}>(({ $left, $color, $background, $borderStyle }) => ({
    position: 'absolute',
    height: '100%',
    left: $left,

    ...($borderStyle == TableBorderStyle.Thin
        ? { width: '1px', background: $color }
        : $borderStyle == TableBorderStyle.Thick
        ? { width: '3px', background: $color, marginLeft: '-1px' }
        : $borderStyle == TableBorderStyle.Double
        ? {
              width: '3px',
              background: $background,
              marginLeft: '-1px',
              borderLeft: `1px solid ${$color}`,
              borderRight: `1px solid ${$color}`,
          }
        : $borderStyle == TableBorderStyle.Hatched
        ? {
              width: '3px',
              marginLeft: '-1px',
              background: `repeating-linear-gradient(-45deg, ${$background} 0 3px, ${$color} 3px 6px)`,
          }
        : {}),
}));

export const GridChartBreak = styled('div')<{
    $left?: string | 0;
    $color?: string;
}>(({ $left, $color }) => ({
    position: 'absolute',
    height: '100%',
    left: $left,
    color: $color,
    zIndex: 10,

    '& svg': {
        display: 'block',
    },
}));

export const GridChartBridge = styled('div')<{ $left?: string | 0; $bridgeHeight?: number }>(
    ({ theme, $left, $bridgeHeight }) => ({
        position: 'absolute',
        width: '1px',
        height: `calc(100% + ${$bridgeHeight}px - 6px)`,
        left: $left,
        top: '3px',
        marginTop: `-${$bridgeHeight}px`,

        background: theme.colors.neutral.w500,
    })
);

export const GridChartBar = styled('div')<{
    $left?: string | 0;
    $width?: string | 0;
    $background?: string;
    $color?: string;
    $border?: string;
    $borderStyle?: SwatchBorderStyle;
    $pattern?: Pattern;
}>(({ theme, $left, $width, $background, $color, $border, $borderStyle, $pattern }) => ({
    position: 'absolute',
    height: 'calc(100% - 6px)',
    left: $left,
    top: '3px',
    width: $width,
    overflow: 'hidden',
    zIndex: 1,

    '> div': {
        position: 'absolute',
        inset: '0px',
        border: {
            [SwatchBorderStyle.None]: 'none',
            [SwatchBorderStyle.Thin]: `1px solid ${$border ?? theme.colors.neutral.w100}`,
            [SwatchBorderStyle.Thick]: `2px solid ${$border ?? theme.colors.neutral.w100}`,
        }[$borderStyle ?? SwatchBorderStyle.None],
        ...($pattern
            ? {
                  [Pattern.None]: { background: $background },
                  [Pattern.Solid]: { background: $color },
                  [Pattern.Hatched]: {
                      background: `repeating-linear-gradient(-45deg, ${$background} 0 3px, ${$color} 3px 6px)`,
                  },
              }[$pattern]
            : undefined),
    },
}));

export const GridChartPinStick = styled('div')<{ $left?: string | 0; $color?: string; $width?: string | 0 }>(
    ({ $left, $color, $width }) => ({
        position: 'absolute',
        height: '3px',
        left: $left,
        top: '50%',
        marginTop: '-1px',
        width: $width,

        background: $color,
    })
);

export const GridChartPinArrowLeft = styled('div')<{
    $left?: string | 0;
    $size?: number;
    $color?: string;
    $shift?: number;
}>(({ $left, $size = 6, $color, $shift = 0 }) => ({
    position: 'absolute',
    width: `${$size}px`,
    height: `${$size}px`,
    left: $left,
    top: '50%',
    marginLeft: `${-Math.floor($size / 2) + $shift}px`,
    marginTop: `${-Math.floor($size / 2)}px`,

    borderTop: `${Math.floor($size / 2)}px solid transparent`,
    borderRight: `${$size}px solid ${$color}`,
    borderBottom: `${Math.floor($size / 2)}px solid transparent`,
}));

export const GridChartPinArrowRight = styled('div')<{
    $left?: string | 0;
    $size?: number;
    $color?: string;
    $shift?: number;
}>(({ $left, $size = 6, $color, $shift = 0 }) => ({
    position: 'absolute',
    width: `${$size}px`,
    height: `${$size}px`,
    left: $left,
    top: '50%',
    marginLeft: `${-Math.floor($size / 2) + $shift}px`,
    marginTop: `${-Math.floor($size / 2)}px`,

    borderTop: `${Math.floor($size / 2)}px solid transparent`,
    borderLeft: `${$size}px solid ${$color}`,
    borderBottom: `${Math.floor($size / 2)}px solid transparent`,
}));

export const GridChartPinTop = styled('div')<{
    $left?: string | 0;
    $size?: number;
    $background?: string;
    $color?: string;
    $border?: string;
    $borderStyle?: SwatchBorderStyle;
    $pattern?: Pattern;
}>(({ theme, $left, $size = 6, $background, $color, $border, $borderStyle, $pattern }) => ({
    position: 'absolute',
    width: `${$size}px`,
    height: `${$size}px`,
    left: $left,
    top: '50%',
    marginLeft: `-${Math.floor($size / 2)}px`,
    marginTop: `-${Math.floor($size / 2)}px`,

    border: {
        [SwatchBorderStyle.None]: 'none',
        [SwatchBorderStyle.Thin]: `1px solid ${$border ?? theme.colors.neutral.w100}`,
        [SwatchBorderStyle.Thick]: `2px solid ${$border ?? theme.colors.neutral.w100}`,
    }[$borderStyle ?? SwatchBorderStyle.None],
    ...($pattern
        ? {
              [Pattern.None]: { background: $background },
              [Pattern.Solid]: { background: $color },
              [Pattern.Hatched]: {
                  background: `repeating-linear-gradient(-45deg, ${$background} 0 3px, ${$color} 3px 6px)`,
              },
          }[$pattern]
        : undefined),
}));

export const GridChartTrafficLights = styled('div')(() => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
}));

export const GridChartLabel = styled(IsolateAbsolute)(() => ({
    zIndex: 1,
}));

export const GridColumnResize = styled('div')(() => ({
    position: 'absolute',
    right: '-5px',
    cursor: 'col-resize',
    width: '10px',
    height: '100%',
    top: 0,
    zIndex: 1,
}));

export const GridRowResize = styled('div')(() => ({
    position: 'absolute',
    bottom: '-5px',
    cursor: 'row-resize',
    height: '10px',
    width: '100%',
    left: 0,
    zIndex: 1,
}));
